import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { DealerUserSearchResult } from './dealer-users/dealerUserService';
import { User } from './users/user';
import { Input } from '@ami/react/core';
import { Dropdown } from '@ami/react/core';
import NotificationSettings from './notificationSettings';
import { DealerUserMultipleSelection } from './dealer-users/DealerUserMultipleSelection';
import { ordersService } from './orders/ordersService';
import { getLoginData } from '@ami/react/core';
import { NotificationScheduleTypes } from './orders/order';

const isDealerEmailAllowed = (
  isEmailCommunicationEnabled,
  dealerUser,
  isEditMode,
  entityName,
  dealerGroupId
) => {
  if (!dealerUser) return false;
  return (
    isEmailCommunicationEnabled &&
    isDealerUserSubscribed(dealerUser, isEditMode, entityName, dealerGroupId)
  );
};

const isDealerUserSubscribed = (
  dealerUser,
  isEditMode,
  entityName,
  dealerGroupId
) => {
  if (!dealerUser) return false;
  const templateKey = getTemplateKey(isEditMode, entityName);
  return dealerUser.dealerGroups.some(
    (x) =>
      x.dealerGroupId === dealerGroupId &&
      x.enabledEmailTemplateTypes.includes(templateKey)
  );
};

const getFilteredAmiUsers = (amiUsers, isEditMode, entityName) => {
  const templateKey = getTemplateKey(isEditMode, entityName);
  return amiUsers.filter((x) =>
    x.enabledEmailTemplateTypes.includes(templateKey)
  );
};

const getTemplateKey = (isEditMode, entityName) => {
  return isEditMode
    ? `NotifyDealer${entityName}Updated`
    : `NotifyDealer${entityName}Created`;
};

export class NotificationSettingsComponent extends React.Component {
  constructor(props) {
    super(props);
    this.clickIncludeCurrentUser = this.clickIncludeCurrentUser.bind(this);
  }

  state = {
    notificationLevels: [],
    notificationSchedules: [],
    visiblePersonalizedMessage: true,
  };

  static propTypes = {
    isEmailCommunicationEnabled: PropTypes.bool.isRequired,
    dealerGroupUsers: PropTypes.arrayOf(
      PropTypes.instanceOf(DealerUserSearchResult)
    ).isRequired,
    dealerUser: PropTypes.instanceOf(DealerUserSearchResult),
    amiUsers: PropTypes.arrayOf(PropTypes.instanceOf(User)).isRequired,
    isEditMode: PropTypes.bool.isRequired,
    notificationSettings: PropTypes.instanceOf(NotificationSettings).isRequired,
    onNotificationSettingsUpdate: PropTypes.func.isRequired,
    entityName: PropTypes.string.isRequired,
    defaultDealerContactIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    dealerGroupId: PropTypes.string.isRequired,
    enabledNotificationSchedule: PropTypes.bool.isRequired,
  };

  componentDidMount() {
    const {
      isEmailCommunicationEnabled,
      dealerUser,
      dealerGroupId,
      isEditMode,
      entityName,
      defaultDealerContactIds,
    } = this.props;
    const dealerEmailAllowed = isDealerEmailAllowed(
      isEmailCommunicationEnabled,
      dealerUser,
      isEditMode,
      entityName,
      dealerGroupId
    );
    this.setDefaults(dealerEmailAllowed, defaultDealerContactIds);
    this.setAvailableLevels(dealerEmailAllowed);
    this.loadNotificationSchedules();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      isEmailCommunicationEnabled,
      dealerUser,
      dealerGroupId,
      isEditMode,
      entityName,
      defaultDealerContactIds,
      notificationSettings,
    } = this.props;
    if (
      prevProps.isEmailCommunicationEnabled !== isEmailCommunicationEnabled ||
      prevProps.dealerUser !== dealerUser ||
      prevProps.defaultDealerContactIds !== defaultDealerContactIds ||
      prevProps.notificationSettings.includeCurrentUser !==
        notificationSettings.includeCurrentUser
    ) {
      const dealerEmailAllowed = isDealerEmailAllowed(
        isEmailCommunicationEnabled,
        dealerUser,
        isEditMode,
        entityName,
        dealerGroupId
      );
      this.setAvailableLevels(dealerEmailAllowed);
      this.setDefaults(dealerEmailAllowed, defaultDealerContactIds);
    }
  }

  async loadNotificationSchedules() {
    const notificationSchedules =
      await ordersService.getNotificationSchedules();
    this.setState({ notificationSchedules: notificationSchedules });
  }

  setAvailableLevels(dealerEmailAllowed) {
    const { amiUsers, isEditMode, entityName, notificationSettings } =
      this.props;
    const anyAmiUsers =
      getFilteredAmiUsers(amiUsers, isEditMode, entityName).length > 0 ||
      notificationSettings.includeCurrentUser;
    const notificationLevels = [];

    if (dealerEmailAllowed) {
      notificationLevels.push({
        id: 'AmiAndDealer',
        label: `Notify Dealer${anyAmiUsers ? ' and all AMI users' : ''}`,
      });
    }
    if (anyAmiUsers) {
      notificationLevels.push({
        id: 'AmiOnly',
        label: 'Notify only AMI users',
      });
    }
    notificationLevels.push({ id: 'Myself', label: 'Notify only me' });
    notificationLevels.push({ id: 'None', label: 'Do not notify anyone' });
    this.setState({ notificationLevels });
    return notificationLevels;
  }

  setDefaults(dealerEmailAllowed, defaultDealerContactIds) {
    const {
      notificationSettings,
      onNotificationSettingsUpdate,
      amiUsers,
      isEditMode,
      entityName,
      dealerUser,
    } = this.props;
    const anyAmiUsers =
      getFilteredAmiUsers(amiUsers, isEditMode, entityName).length > 0;
    const updated = notificationSettings.clone();
    if (updated.notificationLevel !== 'None')
      updated.notificationLevel = dealerEmailAllowed
        ? 'AmiAndDealer'
        : anyAmiUsers
        ? 'AmiOnly'
        : 'Myself';
    if (
      defaultDealerContactIds &&
      updated.secondaryDealerRecipientIds.length === 0
    ) {
      updated.secondaryDealerRecipientIds = defaultDealerContactIds.filter(
        (x) => x !== dealerUser.id
      );
    }
    onNotificationSettingsUpdate(updated);
  }

  clickIncludeCurrentUser() {
    const { notificationSettings, onNotificationSettingsUpdate } = this.props;
    const updated = notificationSettings.clone();
    updated.includeCurrentUser = !updated.includeCurrentUser;
    onNotificationSettingsUpdate(updated);
  }

  render() {
    const {
      isEmailCommunicationEnabled,
      dealerGroupUsers,
      dealerUser,
      amiUsers,
      isEditMode,
      notificationSettings,
      onNotificationSettingsUpdate,
      entityName,
      dealerGroupId,
      enabledNotificationSchedule,
    } = this.props;

    const { visiblePersonalizedMessage } = this.state;

    if (!dealerUser) return null; // if we don't have a dealer user yet, we can't render

    const filteredAmiUsers = getFilteredAmiUsers(
      amiUsers,
      isEditMode,
      entityName
    );
    const loginData = getLoginData();
    if (!loginData) return null;

    return (
      <div className="card-deck">
        <div className="card mb-4 box-shadow">
          <div className="card-body pb-0">
            <h6 className="card-title text-center">Notification Settings</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Notification Level</label>
                  {!isEmailCommunicationEnabled && (
                    <div className="card-body alert alert-danger p-2">
                      <span>Dealer Group email communication disabled</span>
                    </div>
                  )}
                  {!isDealerUserSubscribed(
                    dealerUser,
                    isEditMode,
                    entityName,
                    dealerGroupId
                  ) && (
                    <div className="card-body alert alert-danger p-2">
                      <span>
                        Dealer Contact is not subscribed to this notification
                      </span>
                    </div>
                  )}
                  <Dropdown
                    items={this.state.notificationLevels}
                    onChange={(item) => {
                      const updated = notificationSettings.clone();
                      updated.notificationLevel = item;
                      updated.notificationSchedule =
                        NotificationScheduleTypes.Immediately.id;
                      this.setState({ visiblePersonalizedMessage: true });
                      if (item === 'None') {
                        updated.personalizedMessage = '';
                      }
                      onNotificationSettingsUpdate(updated);
                    }}
                    selectedId={notificationSettings.notificationLevel}
                    className="w-full"
                  />
                </div>

                {enabledNotificationSchedule &&
                  notificationSettings.notificationLevel ===
                    'AmiAndDealer' && (
                    <div className="form-group">
                      <label className="form-label">
                        Notification Schedule
                      </label>
                      <Dropdown
                        items={this.state.notificationSchedules}
                        onChange={(item) => {
                          const updated = notificationSettings.clone();
                          updated.notificationSchedule = item;
                          if (item === NotificationScheduleTypes.Batch.id) {
                            updated.personalizedMessage = '';
                            this.setState({
                              visiblePersonalizedMessage: false,
                            });
                          } else {
                            this.setState({
                              visiblePersonalizedMessage: true,
                            });
                          }
                          onNotificationSettingsUpdate(updated);
                        }}
                        selectedId={notificationSettings.notificationSchedule}
                        className="w-full"
                      />
                    </div>
                  )}
                {notificationSettings.notificationLevel ===
                  'AmiAndDealer' && (
                  <Fragment>
                    <div className="form-group">
                      <label className="form-label">
                        Dealer Recipients
                      </label>
                      <div>
                        {dealerUser.fullName} ({dealerUser.email})
                      </div>
                    </div>
                    <DealerUserMultipleSelection
                      dealerUsers={dealerGroupUsers.filter(
                        (x) => x !== dealerUser.id
                      )}
                      dealerUserIds={
                        notificationSettings.secondaryDealerRecipientIds
                      }
                      onUpdate={(dealerUserIds) => {
                        const updated = notificationSettings.clone();
                        updated.secondaryDealerRecipientIds =
                          dealerUserIds;
                        onNotificationSettingsUpdate(updated);
                      }}
                      fieldLabel="Secondary Dealer Recipients"
                    />
                  </Fragment>
                )}
              </div>
              <div className="col-md-6">
                {(notificationSettings.notificationLevel === 'AmiAndDealer' ||
                  notificationSettings.notificationLevel === 'AmiOnly') &&
                  notificationSettings.notificationSchedule ===
                    NotificationScheduleTypes.Immediately.id && (
                    <div className="row">
                      <div className="col form-group">
                        <label className="form-label">AMI Recipients</label>
                        <ul className="list-unstyled mb-0">
                          <li key={loginData.userData.id}>
                            {notificationSettings.includeCurrentUser ? (
                              loginData.userData.name
                            ) : (
                              <strike>{loginData.userData.name}</strike>
                            )}{' '}
                            <button
                              type="button"
                              className="btn btn-link ps-2 pb-2"
                              onClick={this.clickIncludeCurrentUser}
                            >
                              {notificationSettings.includeCurrentUser
                                ? 'exclude'
                                : 'include'}
                            </button>
                          </li>
                          {filteredAmiUsers
                            .filter((y) => y.id !== loginData.userData.id)
                            .map((x, i) => {
                              return <li key={x.id}>{x.label}</li>;
                            })}
                        </ul>
                      </div>
                    </div>
                  )}
                {notificationSettings.notificationLevel !== 'None' &&
                  notificationSettings.notificationSchedule ===
                    NotificationScheduleTypes.Immediately.id && (
                    <Fragment>
                      <div className="row">
                        <div className="col form-group">
                          <label className="form-label">
                            Other Recipients
                          </label>
                          <Input
                            label="Other Recipients"
                            type="text"
                            placeholder="Comma-separated email addresses"
                            value={notificationSettings.otherRecipients}
                            onChange={(e) => {
                              const updated = notificationSettings.clone();
                              updated.otherRecipients = e.target.value;
                              onNotificationSettingsUpdate(updated);
                            }}
                          />
                        </div>
                      </div>
                      {visiblePersonalizedMessage && (
                        <div className="row">
                          <div className="col form-group">
                            <label className="form-label">
                              Personalized Message
                            </label>
                            <Input
                              type="textarea"
                              rows={2}
                              placeholder="Enter an optional personalized message"
                              value={notificationSettings.personalizedMessage}
                              onChange={(e) => {
                                const updated = notificationSettings.clone();
                                updated.personalizedMessage = e.target.value;
                                onNotificationSettingsUpdate(updated);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </Fragment>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
